import { useFragment } from '@apollo/client';
import { Tracking } from '@ps/isp-web-component-library';
import type { MyBetPart, OutcomeDefinitionExp } from '@ps/isp-web-services-types';
import type { EventTypeId } from '@ps/web-sports-enums';
import React from 'react';

import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';
import type { FootballGameStats, Maybe } from '@swf/generated/graphql/scoreboards/schemas';
import { getEventScoreboardFragment } from '@swf/sports/utils/getEventScoreboardFragment';
import { getIsFootballLiveData } from '@swf/sports/utils/getLiveData';

import { TXTWCL_Selection_progress } from './BetTracking.messages.json';
import styles from './BetTracking.scss';

interface Props {
  eventTypeId: EventTypeId;
  eventId: MyBetPart['eventId'];
  selectionId: MyBetPart['selectionId'];
  outcomeDefinitionExp: MyBetPart['outcomeDefinitionExp'];
}

const getOutcomeDefinitionEntries = (outcomeDefinitionExp: OutcomeDefinitionExp) =>
  outcomeDefinitionExp.outcomeDefinitionEntries;

const BetTracking: React.FC<Props> = ({
  eventTypeId,
  eventId = '',
  outcomeDefinitionExp = {},
  selectionId
}) => {
  const { formatMessage } = useRegulatoryIntl();
  const { data } = useFragment(
    getEventScoreboardFragment(eventTypeId, +eventId, {
      includeTeamStats: true
    })
  );
  const outcomeDefinitionEntries = getOutcomeDefinitionEntries(outcomeDefinitionExp);
  if (!outcomeDefinitionEntries || !eventId || !selectionId) {
    return null;
  }
  return (
    <div className={styles.trackingContainer}>
      {outcomeDefinitionEntries.map((entry, index) => {
        const { outcomeDefinition } = entry;
        if (!outcomeDefinition?.query || !outcomeDefinition.statsThresholdDef) {
          return null;
        }

        const { query, statsThresholdDef } = outcomeDefinition;
        const { threshold } = statsThresholdDef;
        const { periodDefinition, sport, participant, outcome } = query;
        const period = periodDefinition?.period;
        const periodStatus = periodDefinition?.periodStatus;
        const side = participant?.side;

        if (!threshold || !sport || !periodStatus || !period || !side || !outcome) {
          return null;
        }

        const isFootballLiveData = getIsFootballLiveData(eventTypeId, data);
        if (!isFootballLiveData) {
          return null;
        }

        const { stats } = data;
        if (!stats) {
          return null;
        }

        const currentStatsObj = stats.find(
          (stat) => stat?.period === period && stat.periodStatus === periodStatus
        );
        let currentSideObj: Maybe<FootballGameStats> | undefined;
        switch (side) {
          case 'BOTH':
            currentSideObj = currentStatsObj?.both;
            break;
          case 'AWAY':
            currentSideObj = currentStatsObj?.away;
            break;
          case 'HOME':
            currentSideObj = currentStatsObj?.home;
            break;
        }
        if (!currentSideObj) {
          return null;
        }

        const currentValue = currentSideObj[outcome as keyof FootballGameStats];
        if (currentValue === null || currentValue === undefined) {
          return null;
        }

        const maximumValue = Math.ceil(threshold);

        return (
          <Tracking
            key={`${eventId}_${selectionId}_${index}`}
            label={formatMessage(TXTWCL_Selection_progress)}
            currentValue={currentValue}
            maximumValue={maximumValue}
          />
        );
      })}
    </div>
  );
};

export default BetTracking;
